import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, HeartHandshake, Phone } from "lucide-react";
import { WhatsAppIcon } from "../components/icons";
import { ABOUT_IMGS, BRAND } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const FACTS = [
  { value: "2021", label: "Established in Chembur" },
  { value: "~40", label: "Countries personally travelled" },
  { value: "10+", label: "Years on the road" },
  { value: "2–50", label: "Travellers per group" },
];

const PHILOSOPHY = [
  "Most places are personally visited — first-hand experience, not brochure knowledge",
  "Tourist traps are avoided; advice is honest and genuine",
  "All important places covered in the minimum possible cost",
  "No time wasted during the trip — every day counts",
  "Best time to visit and nearby cities smartly clubbed into one itinerary",
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-[72px]">
      {/* HEADER */}
      <section className="noise-dark bg-navy py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
            About Us
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Planned by someone who has actually been there.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Manvi Travels is a travel planner and tour operator based in
            Chembur, Mumbai — experts in all international destinations.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid items-start gap-14 lg:grid-cols-12">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="overflow-hidden rounded-2xl ring-1 ring-black/5">
              <img
                src={ABOUT_IMGS.main}
                alt="Travelling through the Swiss Alps"
                loading="lazy"
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
            </div>
            <div className="mt-4 flex items-center gap-4 rounded-2xl border border-black/5 bg-white p-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gold font-display text-lg font-extrabold text-navy">
                SN
              </div>
              <div>
                <p className="font-display text-lg font-bold text-navy">
                  {BRAND.owner}
                </p>
                <p className="text-sm text-gray-500">
                  Owner, {BRAND.name} — travelled close to 40 countries
                </p>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              The story behind the agency
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-gray-700 sm:text-lg">
              <p>
                Manvi Travels was established in 2021, but it was
                conceptualised long before that — in years of travelling,
                observing how trips are actually sold, and realising how much
                better a holiday can be when planned by someone who has genuinely
                been there.
              </p>
              <p>
                Owner <strong className="text-navy">Sagar Nagpal</strong> has
                been travelling for over a decade and has personally visited
                close to <strong className="text-navy">40 countries</strong> as
                of 2026, with unmatched expertise in planning{" "}
                <strong className="text-navy">entire Europe</strong> and{" "}
                <strong className="text-navy">Japan</strong>. That first-hand
                experience is a real advantage: the right stays, the right
                routes, the right local transport — and an honest opinion on
                what to skip.
              </p>
              <p>
                Whether it is a honeymoon couple of 2 or a corporate group of
                40–50 people, every holiday is completely customised and
                tailor-made to the client&apos;s requirements — and while you
                travel, you have 24/7 support on trip.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {FACTS.map((f, i) => (
                <motion.div
                  key={f.label}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="rounded-2xl border border-black/5 bg-white p-5 text-center"
                >
                  <div className="font-display text-2xl font-extrabold tracking-tight text-coral sm:text-3xl">
                    {f.value}
                  </div>
                  <div className="mt-1 text-xs font-semibold text-gray-500">
                    {f.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-deep">
                How we plan
              </p>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                The Manvi Travels way
              </h2>
              <ul className="mt-8 space-y-4">
                {PHILOSOPHY.map((p, i) => (
                  <motion.li
                    key={p}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal/10">
                      <Check className="h-4 w-4 text-teal" />
                    </span>
                    <span className="text-base leading-relaxed text-gray-700">
                      {p}
                    </span>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-whatsapp-cta"
                  className="flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-display font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                  Start Planning
                </a>
                <a
                  href={BRAND.phoneHref}
                  data-testid="about-phone-cta"
                  className="flex items-center gap-2 rounded-full border-2 border-navy/15 px-7 py-3.5 font-display font-bold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
                >
                  <Phone className="h-5 w-5" />
                  {BRAND.phoneDisplay}
                </a>
              </div>
            </div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-2xl ring-1 ring-black/5">
                <img
                  src={ABOUT_IMGS.secondary}
                  alt="Japan — pagoda and Mount Fuji"
                  loading="lazy"
                  className="h-[380px] w-full object-cover sm:h-[480px]"
                />
              </div>
              <div className="absolute -bottom-6 -left-2 flex items-center gap-3 rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5 sm:-left-6">
                <HeartHandshake className="h-8 w-8 text-coral" />
                <div>
                  <p className="font-display text-base font-bold text-navy">
                    Honest guidance, always
                  </p>
                  <p className="text-xs text-gray-500 sm:text-sm">
                    Your time and money, spent wisely
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:pb-32">
        <div className="rounded-3xl bg-navy px-6 py-16 text-center sm:px-12">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Let&apos;s plan your next one properly.
          </h2>
          <Link
            to="/destinations"
            data-testid="about-destinations-link"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-display font-bold text-navy transition-transform duration-200 hover:scale-[1.03]"
          >
            Browse Destinations
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
